import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import zh from './zh';
import en from './en';

export type Language = 'zh' | 'en';

const translations: Record<Language, Record<string, string>> = { zh, en };

// Detect browser language
function detectBrowserLanguage(): Language {
  if (typeof navigator === 'undefined') return 'zh';
  const browserLang = navigator.language || (navigator as any).userLanguage || '';
  // Chinese variants: zh, zh-CN, zh-TW, zh-HK, etc.
  if (browserLang.startsWith('zh')) return 'zh';
  return 'en';
}

// Global language state: saved preference > browser detection
let currentLanguage: Language = (() => {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('language');
    if (saved === 'zh' || saved === 'en') return saved;
  }
  return detectBrowserLanguage();
})();

// Global translate function for use outside React components (e.g., in models/services)
export function t(key: string, params?: Record<string, string | number>): string {
  const template = translations[currentLanguage]?.[key] || key;
  if (params) {
    return Object.entries(params).reduce(
      (str, [k, v]) => str.replace(`{${k}}`, String(v)),
      template
    );
  }
  return template;
}

export function getLanguage(): Language {
  return currentLanguage;
}

// React context
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

export const LanguageContext = createContext<LanguageContextType>({
  language: 'zh',
  setLanguage: () => {},
  t,
});

export function useLanguage() {
  return useContext(LanguageContext);
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(currentLanguage);

  const setLanguage = useCallback((lang: Language) => {
    currentLanguage = lang;
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    // Update HTML lang attribute
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : 'en';
  }, []);

  useEffect(() => {
    document.documentElement.lang = language === 'zh' ? 'zh-CN' : 'en';
  }, [language]);

  const translate = useCallback((key: string, params?: Record<string, string | number>): string => {
    const template = translations[language]?.[key] || key;
    if (params) {
      return Object.entries(params).reduce(
        (str, [k, v]) => str.replace(`{${k}}`, String(v)),
        template
      );
    }
    return template;
  }, [language]);

  return React.createElement(
    LanguageContext.Provider,
    { value: { language, setLanguage, t: translate } },
    children
  );
}
